import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getValidToken } from '../utils/authUtils';

const AdminRecruiterStatsReport = () => {
  const navigate = useNavigate();

  // Data state
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 300;

  // Filter state
  const [cycleYear, setCycleYear] = useState(null);
  const [availableCycles, setAvailableCycles] = useState([]);
  const [currentCycleYear, setCurrentCycleYear] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Sorting state
  const [sortBy, setSortBy] = useState('organization');
  const [sortOrder, setSortOrder] = useState('asc');

  // Export state
  const [exporting, setExporting] = useState(false);

  // Check authentication
  useEffect(() => {
    const token = getValidToken();
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Fetch available cycles on mount
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        const token = getValidToken();
        const response = await axios.get(`${API_URL}/api/admin/recruiter-stats-report/cycles`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setAvailableCycles(response.data.cycles);
          setCurrentCycleYear(response.data.current_cycle_year);
          // Set initial cycle year to current
          if (response.data.current_cycle_year) {
            setCycleYear(response.data.current_cycle_year);
          } else if (response.data.cycles.length > 0) {
            setCycleYear(response.data.cycles[0].year);
          }
        }
      } catch (err) {
        console.error('Error fetching cycles:', err);
        setError('Failed to load cycle years');
      }
    };
    fetchCycles();
  }, []);

  // Fetch recruiters data
  const fetchRecruiters = useCallback(async () => {
    if (!cycleYear) return;

    setLoading(true);
    setError(null);

    try {
      const token = getValidToken();
      const params = new URLSearchParams({
        cycle_year: cycleYear,
        page: currentPage,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedOrganization) {
        params.append('organization', selectedOrganization);
      }

      const response = await axios.get(
        `${API_URL}/api/admin/recruiter-stats-report?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setRecruiters(response.data.recruiters);
        setTotalPages(response.data.pagination.total_pages);
        setTotalRecords(response.data.pagination.total_records);
        setCurrentPage(response.data.pagination.page);
        setOrganizations(response.data.filters.organizations);
      }
    } catch (err) {
      console.error('Error fetching recruiters:', err);
      setError('Failed to load recruiter data');
    } finally {
      setLoading(false);
    }
  }, [cycleYear, currentPage, sortBy, sortOrder, searchTerm, selectedOrganization]);

  // Fetch recruiters when dependencies change
  useEffect(() => {
    fetchRecruiters();
  }, [fetchRecruiters]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return <span className="text-gray-300 ml-1">↕</span>;
    }
    return sortOrder === 'asc'
      ? <span className="text-blue-600 ml-1">↑</span>
      : <span className="text-blue-600 ml-1">↓</span>;
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      const token = getValidToken();
      const params = new URLSearchParams({
        cycle_year: cycleYear,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedOrganization) {
        params.append('organization', selectedOrganization);
      }

      const response = await axios.get(
        `${API_URL}/api/admin/recruiter-stats-report/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recruiter_stats_report_cycle${cycleYear}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Handle cycle year change
  const handleCycleChange = (e) => {
    setCycleYear(parseInt(e.target.value));
    setCurrentPage(1);
    setSelectedOrganization('');
  };

  // Handle organization filter change
  const handleOrganizationChange = (e) => {
    setSelectedOrganization(e.target.value);
    setCurrentPage(1);
  };

  // Pagination component
  const Pagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 my-4">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ◀◀
        </button>
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ◀ Prev
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              className="px-3 py-1 rounded border bg-white hover:bg-gray-100"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2">...</span>}
          </>
        )}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`px-3 py-1 rounded border ${
              currentPage === page
                ? 'bg-blue-600 text-white'
                : 'bg-white hover:bg-gray-100'
            }`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2">...</span>}
            <button
              onClick={() => setCurrentPage(totalPages)}
              className="px-3 py-1 rounded border bg-white hover:bg-gray-100"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next ▶
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ▶▶
        </button>
      </div>
    );
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Format datetime for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Recruiter Stats Report</h1>
              <p className="text-sm text-gray-600 mt-1">View and export recruiter activity statistics by recruitment cycle</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border rounded hover:bg-gray-50"
            >
              ← Back to Admin
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Cycle Year Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Year</label>
              <select
                value={cycleYear || ''}
                onChange={handleCycleChange}
                className="px-3 py-2 border rounded-md min-w-[180px] bg-white"
              >
                {availableCycles.map(cycle => (
                  <option key={cycle.year} value={cycle.year}>
                    {cycle.year} {cycle.is_current ? '(Current)' : ''} - {cycle.recruiter_count} recruiters
                  </option>
                ))}
              </select>
            </div>

            {/* Organization Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
              <select
                value={selectedOrganization}
                onChange={handleOrganizationChange}
                className="px-3 py-2 border rounded-md min-w-[200px] bg-white"
              >
                <option value="">All Organizations</option>
                {organizations.map(org => (
                  <option key={org} value={org}>{org}</option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name, email, or organization..."
                className="px-3 py-2 border rounded-md min-w-[280px]"
              />
            </div>

            {/* Export Button */}
            <div className="ml-auto">
              <button
                onClick={handleExport}
                disabled={exporting || loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {exporting ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Exporting...
                  </>
                ) : (
                  <>
                    📥 Export CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Pagination Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-blue-600 text-lg">ℹ️</span>
              <span className="text-blue-800 font-medium">
                Displaying {perPage} records per page | Page {currentPage} of {totalPages} | {totalRecords.toLocaleString()} total recruiters
              </span>
            </div>
            {cycleYear === currentCycleYear && (
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                Current Cycle
              </span>
            )}
          </div>
        </div>

        {/* Top Pagination */}
        {totalPages > 1 && <Pagination />}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Data Table - Fixed height with sticky columns */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-auto max-h-[600px]" style={{ position: 'relative' }}>
            <table className="min-w-full divide-y divide-gray-200 border-collapse">
              <thead className="bg-gray-50 sticky top-0 z-30">
                <tr>
                  {/* Sticky columns: ID, Organization, Name */}
                  <th
                    onClick={() => handleSort('recruiter_id')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 sticky left-0 z-20"
                    style={{ minWidth: '70px' }}
                  >
                    ID {renderSortIcon('recruiter_id')}
                  </th>
                  <th
                    onClick={() => handleSort('organization')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 sticky z-20"
                    style={{ left: '70px', minWidth: '150px' }}
                  >
                    Organization {renderSortIcon('organization')}
                  </th>
                  <th
                    onClick={() => handleSort('recruiter_name')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 sticky z-20 border-r-2 border-gray-300"
                    style={{ left: '220px', minWidth: '130px' }}
                  >
                    Name {renderSortIcon('recruiter_name')}
                  </th>
                  {/* Non-sticky columns */}
                  <th
                    onClick={() => handleSort('email')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Email {renderSortIcon('email')}
                  </th>
                  <th
                    onClick={() => handleSort('shortlisted_count')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Shortlisted {renderSortIcon('shortlisted_count')}
                  </th>
                  <th
                    onClick={() => handleSort('favorited_count')}
                    className="px-4 py-3 text-center text-xs font-medium text-yellow-600 uppercase tracking-wider cursor-pointer hover:bg-yellow-50 bg-yellow-50"
                  >
                    Favorited {renderSortIcon('favorited_count')}
                  </th>
                  <th
                    onClick={() => handleSort('applied_count')}
                    className="px-4 py-3 text-center text-xs font-medium text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-blue-50 bg-blue-50"
                  >
                    Applied {renderSortIcon('applied_count')}
                  </th>
                  <th
                    onClick={() => handleSort('matched_count')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Matched {renderSortIcon('matched_count')}
                  </th>
                  <th
                    onClick={() => handleSort('profile_requests_sent')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Profile Req {renderSortIcon('profile_requests_sent')}
                  </th>
                  <th
                    onClick={() => handleSort('links_sent')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Links {renderSortIcon('links_sent')}
                  </th>
                  <th
                    onClick={() => handleSort('interviews_sent')}
                    className="px-4 py-3 text-center text-xs font-medium text-purple-600 uppercase tracking-wider cursor-pointer hover:bg-purple-50 bg-purple-50"
                  >
                    Interviews {renderSortIcon('interviews_sent')}
                  </th>
                  <th
                    onClick={() => handleSort('offers_sent')}
                    className="px-4 py-3 text-center text-xs font-medium text-orange-600 uppercase tracking-wider cursor-pointer hover:bg-orange-50 bg-orange-50"
                  >
                    Offers {renderSortIcon('offers_sent')}
                  </th>
                  <th
                    onClick={() => handleSort('offers_accepted')}
                    className="px-4 py-3 text-center text-xs font-medium text-green-600 uppercase tracking-wider cursor-pointer hover:bg-green-50 bg-green-50"
                  >
                    Accepted {renderSortIcon('offers_accepted')}
                  </th>
                  <th
                    onClick={() => handleSort('offers_declined')}
                    className="px-4 py-3 text-center text-xs font-medium text-red-600 uppercase tracking-wider cursor-pointer hover:bg-red-50 bg-red-50"
                  >
                    Declined {renderSortIcon('offers_declined')}
                  </th>
                  <th
                    onClick={() => handleSort('internship_length')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Length (wks) {renderSortIcon('internship_length')}
                  </th>
                  <th
                    onClick={() => handleSort('expected_start_date')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Start Date {renderSortIcon('expected_start_date')}
                  </th>
                  <th
                    onClick={() => handleSort('last_activity')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Last Activity {renderSortIcon('last_activity')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="17" className="px-4 py-8 text-center text-gray-500">
                      <div className="flex items-center justify-center gap-2">
                        <span className="animate-spin text-2xl">⏳</span>
                        Loading recruiters...
                      </div>
                    </td>
                  </tr>
                ) : recruiters.length === 0 ? (
                  <tr>
                    <td colSpan="17" className="px-4 py-8 text-center text-gray-500">
                      No recruiters found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  recruiters.map((recruiter, index) => (
                    <tr key={recruiter.recruiter_id || index} className="hover:bg-gray-50 group">
                      {/* Sticky columns with background */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50 sticky left-0 z-10" style={{ minWidth: '70px' }}>
                        {recruiter.recruiter_id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50 sticky z-10" style={{ left: '70px', minWidth: '150px' }}>
                        <span className="truncate block max-w-[140px]" title={recruiter.organization}>
                          {recruiter.organization}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50 sticky z-10 border-r-2 border-gray-200" style={{ left: '220px', minWidth: '130px' }}>
                        {recruiter.recruiter_name}
                      </td>
                      {/* Non-sticky columns */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        <a
                          href={`mailto:${recruiter.email}`}
                          className="text-blue-600 hover:underline"
                        >
                          {recruiter.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                        {recruiter.shortlisted_count}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-yellow-50">
                        <span className={`font-medium ${recruiter.favorited_count > 0 ? 'text-yellow-700' : 'text-gray-400'}`}>
                          {recruiter.favorited_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-blue-50">
                        <span className={`font-medium ${recruiter.applied_count > 0 ? 'text-blue-700' : 'text-gray-400'}`}>
                          {recruiter.applied_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                        {recruiter.matched_count}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                        {recruiter.profile_requests_sent}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                        {recruiter.links_sent}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-purple-50">
                        <span className={`font-medium ${recruiter.interviews_sent > 0 ? 'text-purple-700' : 'text-gray-400'}`}>
                          {recruiter.interviews_sent}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-orange-50">
                        <span className={`font-medium ${recruiter.offers_sent > 0 ? 'text-orange-700' : 'text-gray-400'}`}>
                          {recruiter.offers_sent}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-green-50">
                        <span className={`font-bold ${recruiter.offers_accepted > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                          {recruiter.offers_accepted}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-red-50">
                        <span className={`font-medium ${recruiter.offers_declined > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                          {recruiter.offers_declined}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                        {recruiter.internship_length || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                        {formatDate(recruiter.expected_start_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                        {formatDateTime(recruiter.last_activity)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Pagination */}
        {totalPages > 1 && <Pagination />}
      </main>
    </div>
  );
};

export default AdminRecruiterStatsReport;
